import React, {useEffect, useState} from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircle from '@mui/icons-material/AccountCircle';
import CreateIcon from '@mui/icons-material/Create';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MoreIcon from '@mui/icons-material/MoreVert';
import { useCookies } from 'react-cookie';
import LoginIcon from '@mui/icons-material/Login';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Divider, List, ListItem, ListItemAvatar, ListItemText, Paper, Tooltip } from '@mui/material';
import Notifications from './Notifications';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

export default function NavBar({socket}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
  const [cookies, setCookies, removeCookie] = useCookies();
  const [hideLogin, setHideLogin] = useState(false);
  const [notifications, setNotifications] = useState([])
  const [openNotifs, setOpenNotifs] = useState(false)
  const navigate = useNavigate();

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  useEffect(()=>{
    socket?.on("notifications", (data)=>{
      setNotifications((prev)=>[...prev, data])
    })
  }, [socket])

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = (event) => {
    console.log(event)

    if(event.target.id === 'publish'){
      handleBlogPublish()
    }
    else if(event.target.id === 'dashboard'){
      navigate('/dashboard')
    }
    else if(event.target.id === 'account'){
      const userId = cookies['userId']
      if(!userId){
        navigate('/login')
        return
      }
      navigate( `/account/${userId}`)
    }
    else if(event.target.id === 'logout'){
      removeCookie('accessToken')
      removeCookie('userId')
      navigate('/')
      socket.emit("disconnect", (cookies['userId']))
      window.location.reload();
    }
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const loginUser = (event) => {
    console.log(event.target)
    setHideLogin(true)
    navigate("/login")
  }

  const handleBlogPublish = () =>{
    navigate("/publish")
  }

  const searchResults = (event) =>{
    const key = event.target.value
    console.log(key)
    if(event.keyCode == 13){
      navigate("/search", {state:{searchKey:key}})
      event.target.value = ""
    }
  }

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem id="dashboard" onClick={handleMenuClose}>Dashboard</MenuItem>
      <MenuItem id="account" onClick={handleMenuClose}>My account</MenuItem>
      <MenuItem id="logout" onClick={handleMenuClose}>Logout</MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem id="publish" onClick={handleMenuClose}>
        <IconButton 
          size="large" 
          color="inherit" 
        >
          <CreateIcon/>
        </IconButton>
        <p>Publish</p>
      </MenuItem>
      <MenuItem id="notifications" onClick={handleMenuClose}>
        <IconButton
          size="large"
          color="inherit"
          onClick={() => setOpenNotifs(!openNotifs)}
        >
          <Badge badgeContent={notifications.length} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  return (
    <div className='navBar'>
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography
            size="large"
            edge="start"
            color="inherit"
            sx={{ mr: 2 }}
          >
            <Link to={"/"} className='blogIcon'>B</Link>
          </Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ 'aria-label': 'search' }}
              onKeyDown={searchResults}
            />
          </Search>            
          <Box sx={{ flexGrow: 1 }} />
          { 
            !cookies['accessToken'] && !hideLogin &&
            <Tooltip title='Login'>
              <IconButton size="large" color="inherit" onClick={loginUser}>
                <LoginIcon/>
              </IconButton>
            </Tooltip>
          }
          {cookies['accessToken'] &&
          <section style={{float: 'right'}}>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Tooltip title='Publish'>
                <IconButton onClick={handleBlogPublish} size="large" aria-label="show 4 new mails" color="inherit">
                  <CreateIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title='Notifications'>
              <IconButton
                size="large"
                color="inherit"
                onClick={() => setOpenNotifs(!openNotifs)}
              >
              <Badge badgeContent={notifications.length} color="error">
                <NotificationsIcon />
              </Badge>
              </IconButton>
              </Tooltip>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
            </Box>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                color="inherit"
              >
                <MoreIcon />
              </IconButton>
            </Box>
          </section>
          }
        </Toolbar>
      </AppBar>
      <Notifications
       openNotifs={openNotifs} 
       notifications={notifications} 
       setNotifications={setNotifications}
       setOpenNotifs={setOpenNotifs}
       />
      {renderMobileMenu}
      {renderMenu}
    </Box>
    </div>
  );
}